import { actions as companyActions } from 'store/modules/companies'
import notification from 'modules/app-core/notification-manager'
import { browserHistory } from 'react-router'
import * as selectors from './selectors'
import {
  isRestorableUrl,
  locationToUrl,
  urlToLocation,
} from './utils/currentUrl'

// module
import t from './actionTypes.js'

// auth-ui may hold a stale redirectRoute (often `/login` after logout). Clearing
// it prevents AuthenticatedComponent from undoing our post-login restore.
const clearAuthRedirectRoute = () => ({
  type: 'authui/REMOVE_REDIRECT_ROUTE',
})

export const userInitialized = () => ({
  type: t.USER_INITIALIZED,
})

export const setCurrentCompany = (companyId) => ({
  type: t.SET_CURRENT_COMPANY,
  payload: companyId,
})

export const setQle = (qleId) => ({
  type: t.SET_QLE,
  payload: qleId,
})

export const setCurrentUrl = (url) => ({
  type: t.SET_CURRENT_URL,
  payload: url,
})

export const clearCurrentUrl = () => ({
  type: t.CLEAR_CURRENT_URL,
})

/**
 * Restore saved `currentUrl` after re-login only.
 * Called solely from `restoreUrlAfterLogin` after a successful login request.
 *
 * Deep links entered before login are handled by auth-ui `redirectRoute` —
 * this must not override that.
 */
export const restoreCurrentUrl = () => (dispatch, getState) => {
  const state = getState()
  const authRedirect = state.authui && state.authui.redirectRoute

  // Pre-login deep link: let auth-ui AuthenticatedComponent push redirectRoute.
  if (authRedirect && isRestorableUrl(authRedirect)) {
    return
  }

  const saved = selectors.getCurrentUrl(state)
  const location = browserHistory.getCurrentLocation()
  const current = locationToUrl({
    pathname: location.pathname,
    search: location.search || window.location.search,
    query: location.query,
  })

  // Already on a non-root restorable page (e.g. auth deep link) — do not navigate away.
  // `/` is restorable for persistence, but post-login still needs to restore a saved URL.
  const currentPathname = location.pathname || ''
  if (
    isRestorableUrl(current) &&
    currentPathname !== '/' &&
    currentPathname !== ''
  ) {
    return
  }

  // Re-login landed on `/` (LoginContainer default) — restore last saved page.
  if (saved && isRestorableUrl(saved) && saved !== current) {
    // Drop stale auth redirect (often `/login`) so it cannot undo this navigate.
    dispatch(clearAuthRedirectRoute())
    dispatch(clearCurrentUrl())
    browserHistory.replace(urlToLocation(saved))
    return
  }

  if (saved && !isRestorableUrl(saved)) {
    dispatch(clearCurrentUrl())
  }
}

export const initializeUser = () => (dispatch, getState) => {
  return dispatch(companyActions.getAllCompanies())
    .then(async (companies) => {
      dispatch(userInitialized())
      const clients = companies

      if (clients.length > 0) {
        const currentCompanyId = selectors.getCurrentCompanyId(getState())

        const foundCompany = clients.find(
          (company) => company.id === currentCompanyId
        )

        if (!currentCompanyId || !foundCompany) {
          dispatch(setCurrentCompany(clients[0].id))
        }
      } else {
        dispatch(
          notification.actions.createNotification({
            type: 'error',
            message: "You don't have access to any companies.",
          })
        )
        browserHistory.push('/session-error')
      }
    })
    .catch(() => {
      dispatch(
        notification.actions.createNotification({
          type: 'error',
          message:
            'We had trouble loading your session. Please reload the page',
        })
      )

      browserHistory.push('/session-error')
    })
}
